import { ABI_VERSION, GLYPH_BITMAP_MINIMUM_BYTES, GLYPH_PLACEMENT_MINIMUM_BYTES, GLYPH_RESOURCE_LAYOUTS, GLYPH_RESOURCES_MAGIC, GlyphResourceOpcode, INSTRUCTION_FLAG_MASK, INSTRUCTION_FLAG_OPTIONAL, INSTRUCTION_HEADER_BYTES, INSTRUCTION_LENGTH_ESCAPE, MINIMUM_READABLE_ABI_VERSION, MAX_GLYPH_BITMAP_PIXELS, MAX_GLYPH_RESOURCE_INSTRUCTIONS, MAX_GLYPH_RESOURCES_BYTES, PROTOCOL_ALIGNMENT, STREAM_HEADER_BYTES, } from "./generated";
/** Deterministic validation failure raised before backend state changes. */
export class GlyphResourceError extends Error {
    constructor(message) {
        super(message);
        this.name = "GlyphResourceError";
    }
}
/** Decodes a complete glyph-resource batch without mutating a registry. */
export function decodeGlyphResourceBatch(input) {
    if (input.byteLength > MAX_GLYPH_RESOURCES_BYTES)
        fail("glyph batch exceeds maximum size");
    if (input.byteLength % PROTOCOL_ALIGNMENT !== 0)
        fail("glyph batch is not aligned");
    const reader = new Reader(input);
    if (reader.u32() !== GLYPH_RESOURCES_MAGIC)
        fail("wrong glyph-resource magic");
    // Newer producers stay readable through the self-describing instruction
    // framing; anything older than it cannot be stepped through safely.
    if (reader.u16() < MINIMUM_READABLE_ABI_VERSION)
        fail("unsupported glyph-resource ABI version");
    if (reader.u16() !== STREAM_HEADER_BYTES)
        fail("invalid glyph-resource header length");
    if (reader.u32() !== input.byteLength)
        fail("glyph-resource length does not match input");
    const declaredCount = reader.u32();
    if (declaredCount > MAX_GLYPH_RESOURCE_INSTRUCTIONS)
        fail("glyph-resource instruction count exceeds limit");
    if (declaredCount > Math.floor(reader.remaining / 8))
        fail("glyph-resource count cannot fit in the remaining bytes");
    const deltas = [];
    const seen = new Set();
    while (reader.remaining > 0) {
        const start = reader.offset;
        const header = reader.instruction();
        if (!isKnownOpcode(GlyphResourceOpcode, header.opcode)) {
            // Skipping is the producer's call: a glyph span this build cannot decode
            // is only safe to drop when the producer said so.
            if (!header.optional)
                fail(`unknown glyph-resource opcode ${String(header.opcode)}`);
            reader.seekTo(header.end);
            continue;
        }
        const opcode = header.opcode;
        const delta = opcode === GlyphResourceOpcode.DefineGlyphSpan
            ? { type: "define", span: decodeSpan(reader) }
            : { type: "release", spanId: nonzeroId(reader.u32(), "span") };
        const spanId = delta.type === "define" ? delta.span.spanId : delta.spanId;
        if (seen.has(spanId))
            fail("glyph span id occurs more than once in a batch");
        seen.add(spanId);
        const layout = GLYPH_RESOURCE_LAYOUTS[opcode];
        const consumed = reader.offset - start;
        if (consumed < layout.minimumBytes ||
            (layout.fixedBytes !== null && consumed !== layout.fixedBytes)) {
            fail("glyph-resource instruction length does not match schema");
        }
        if (reader.offset !== header.end)
            fail("instruction length does not match its payload");
        deltas.push(delta);
    }
    if (deltas.length !== declaredCount)
        fail("glyph-resource instruction count does not match");
    return deltas;
}
/** Encodes canonical bytes for cross-language fixtures and Core conformance tests. */
export function encodeGlyphResourceBatch(deltas) {
    if (deltas.length > MAX_GLYPH_RESOURCE_INSTRUCTIONS)
        fail("glyph-resource instruction count exceeds limit");
    const writer = new Writer();
    writer.u32(GLYPH_RESOURCES_MAGIC);
    writer.u16(ABI_VERSION);
    writer.u16(STREAM_HEADER_BYTES);
    writer.u32(0);
    writer.u32(deltas.length);
    const seen = new Set();
    for (const delta of deltas) {
        const spanId = delta.type === "define" ? delta.span.spanId : delta.spanId;
        nonzeroId(spanId, "span");
        if (seen.has(spanId))
            fail("glyph span id occurs more than once in a batch");
        seen.add(spanId);
        writer.instruction(delta.type === "define"
            ? GlyphResourceOpcode.DefineGlyphSpan
            : GlyphResourceOpcode.ReleaseGlyphSpan);
        if (delta.type === "release") {
            writer.u32(delta.spanId);
            continue;
        }
        const { span } = delta;
        nonzeroId(span.paintId, "paint");
        const payloadBytes = spanPayloadBytes(span);
        writer.u32(span.spanId);
        writer.u32(span.paintId);
        writer.u32(span.bitmaps.length);
        writer.u32(span.placements.length);
        writer.u32(payloadBytes);
        for (const bitmap of span.bitmaps) {
            validateBitmap(bitmap);
            writer.u16(bitmap.glyphId);
            writer.u16(0);
            writer.f32(bitmap.left);
            writer.f32(bitmap.top);
            writer.u32(bitmap.width);
            writer.u32(bitmap.height);
            writer.f32(bitmap.devicePixelRatio);
            writer.u32(bitmap.data.byteLength);
            writer.bytes(bitmap.data);
            writer.pad();
        }
        for (const placement of span.placements) {
            if (!Number.isInteger(placement.bitmapIndex) ||
                placement.bitmapIndex < 0 ||
                placement.bitmapIndex >= span.bitmaps.length)
                fail("glyph placement bitmap index is out of bounds");
            writer.u32(placement.bitmapIndex);
            writer.f32(placement.x);
            writer.f32(placement.y);
        }
    }
    const output = writer.finish();
    if (output.byteLength > MAX_GLYPH_RESOURCES_BYTES)
        fail("glyph batch exceeds maximum size");
    new DataView(output.buffer, output.byteOffset, output.byteLength).setUint32(8, output.byteLength, true);
    return output;
}
function decodeSpan(reader) {
    const spanId = nonzeroId(reader.u32(), "span");
    const paintId = nonzeroId(reader.u32(), "paint");
    const bitmapCount = reader.u32();
    const glyphCount = reader.u32();
    const payloadBytes = reader.u32();
    if (payloadBytes % PROTOCOL_ALIGNMENT !== 0)
        fail("glyph span payload is not aligned");
    const minimum = checkedAdd(checkedMultiply(bitmapCount, GLYPH_BITMAP_MINIMUM_BYTES), checkedMultiply(glyphCount, GLYPH_PLACEMENT_MINIMUM_BYTES));
    if (minimum > payloadBytes)
        fail("glyph span counts cannot fit in the payload");
    const payload = new Reader(reader.bytes(payloadBytes));
    const bitmaps = Array.from({ length: bitmapCount }, () => decodeBitmap(payload));
    const placements = Array.from({ length: glyphCount }, () => {
        const bitmapIndex = payload.u32();
        if (bitmapIndex >= bitmaps.length)
            fail("glyph placement bitmap index is out of bounds");
        return Object.freeze({ bitmapIndex, x: payload.f32(), y: payload.f32() });
    });
    if (payload.remaining !== 0)
        fail("glyph span payload has trailing bytes");
    return Object.freeze({
        spanId,
        paintId,
        bitmaps: Object.freeze(bitmaps),
        placements: Object.freeze(placements),
    });
}
function decodeBitmap(reader) {
    const glyphId = reader.u16();
    if (reader.u16() !== 0)
        fail("glyph bitmap reserved bytes must be zero");
    const left = reader.f32();
    const top = reader.f32();
    const width = reader.u32();
    const height = reader.u32();
    const devicePixelRatio = reader.f32();
    if (devicePixelRatio <= 0)
        fail("glyph bitmap DPR must be positive");
    const dataBytes = reader.u32();
    const pixels = checkedMultiply(width, height);
    if (pixels === 0 || pixels > MAX_GLYPH_BITMAP_PIXELS || dataBytes !== pixels) {
        fail("glyph bitmap must contain one bounded alpha byte per pixel");
    }
    const data = reader.bytes(dataBytes).slice();
    const padding = (PROTOCOL_ALIGNMENT - (dataBytes % PROTOCOL_ALIGNMENT)) % PROTOCOL_ALIGNMENT;
    for (const byte of reader.bytes(padding)) {
        if (byte !== 0)
            fail("glyph bitmap padding must be zero");
    }
    return Object.freeze({ glyphId, left, top, width, height, devicePixelRatio, data });
}
function spanPayloadBytes(span) {
    let bytes = checkedMultiply(span.placements.length, GLYPH_PLACEMENT_MINIMUM_BYTES);
    for (const bitmap of span.bitmaps) {
        validateBitmap(bitmap);
        bytes = checkedAdd(bytes, GLYPH_BITMAP_MINIMUM_BYTES);
        bytes = checkedAdd(bytes, bitmap.data.byteLength);
        bytes = checkedAdd(bytes, (PROTOCOL_ALIGNMENT - (bitmap.data.byteLength % PROTOCOL_ALIGNMENT)) % PROTOCOL_ALIGNMENT);
    }
    return bytes;
}
function validateBitmap(bitmap) {
    if (!Number.isInteger(bitmap.glyphId) || bitmap.glyphId < 0 || bitmap.glyphId > 0xffff)
        fail("glyph id is outside u16");
    if (!Number.isInteger(bitmap.width) ||
        bitmap.width < 0 ||
        bitmap.width > 0xffff_ffff ||
        !Number.isInteger(bitmap.height) ||
        bitmap.height < 0 ||
        bitmap.height > 0xffff_ffff ||
        !Number.isFinite(bitmap.left) ||
        !Number.isFinite(bitmap.top) ||
        !Number.isFinite(bitmap.devicePixelRatio) ||
        bitmap.devicePixelRatio <= 0) {
        fail("glyph bitmap geometry and DPR must be finite and positive");
    }
    const pixels = checkedMultiply(bitmap.width, bitmap.height);
    if (pixels === 0 || pixels > MAX_GLYPH_BITMAP_PIXELS || pixels !== bitmap.data.byteLength)
        fail("glyph bitmap must contain one bounded alpha byte per pixel");
}
class Reader {
    #input;
    #view;
    #offset = 0;
    constructor(input) {
        this.#input = input;
        this.#view = new DataView(input.buffer, input.byteOffset, input.byteLength);
    }
    get offset() {
        return this.#offset;
    }
    get remaining() {
        return this.#input.byteLength - this.#offset;
    }
    /** Reads one instruction header, including where the instruction ends. */
    instruction() {
        const offset = this.#offset;
        if (offset % PROTOCOL_ALIGNMENT !== 0)
            fail("instruction is not aligned");
        this.require(INSTRUCTION_HEADER_BYTES);
        const opcode = this.u8();
        const flags = this.u8();
        if ((flags & ~INSTRUCTION_FLAG_MASK) !== 0)
            fail("unsupported instruction flags");
        const words = this.u16();
        const length = words === INSTRUCTION_LENGTH_ESCAPE ? this.u32() : words * PROTOCOL_ALIGNMENT;
        const end = offset + length;
        if (length < INSTRUCTION_HEADER_BYTES || length % PROTOCOL_ALIGNMENT !== 0) {
            fail("instruction length is invalid");
        }
        if (end > this.#input.byteLength)
            fail("instruction length runs past the stream");
        return { opcode, optional: (flags & INSTRUCTION_FLAG_OPTIONAL) !== 0, end };
    }
    /** Moves the cursor forward to an instruction boundary. */
    seekTo(offset) {
        if (offset < this.#offset || offset > this.#input.byteLength)
            fail("invalid instruction skip");
        this.#offset = offset;
    }
    u8() {
        this.require(1);
        return this.#view.getUint8(this.#offset++);
    }
    u16() {
        this.require(2);
        const value = this.#view.getUint16(this.#offset, true);
        this.#offset += 2;
        return value;
    }
    u32() {
        this.require(4);
        const value = this.#view.getUint32(this.#offset, true);
        this.#offset += 4;
        return value;
    }
    f32() {
        this.require(4);
        const value = this.#view.getFloat32(this.#offset, true);
        this.#offset += 4;
        if (!Number.isFinite(value))
            fail("glyph-resource float must be finite");
        return value;
    }
    bytes(length) {
        this.require(length);
        const value = this.#input.subarray(this.#offset, this.#offset + length);
        this.#offset += length;
        return value;
    }
    require(length) {
        if (!Number.isSafeInteger(length) || length < 0 || length > this.remaining)
            fail("truncated glyph-resource batch");
    }
}
class Writer {
    #bytes = [];
    #instructionStart;
    /** Opens an instruction; its length is patched in when the next one starts. */
    instruction(opcode) {
        this.closeInstruction();
        this.#instructionStart = this.#bytes.length;
        this.u8(opcode);
        this.u8(0);
        this.u16(0);
    }
    /**
     * Writes the length of the instruction that just ended.
     *
     * A glyph span carries its bitmaps inline and can exceed the 256 KiB a `u16`
     * word count reaches, so an oversized one writes the escape and carries its
     * byte length in the word right after the header.
     */
    closeInstruction() {
        const start = this.#instructionStart;
        if (start === undefined)
            return;
        this.#instructionStart = undefined;
        const length = this.#bytes.length - start;
        const words = length / PROTOCOL_ALIGNMENT;
        if (words < INSTRUCTION_LENGTH_ESCAPE) {
            this.#bytes[start + 2] = words & 0xff;
            this.#bytes[start + 3] = (words >>> 8) & 0xff;
            return;
        }
        this.#bytes[start + 2] = INSTRUCTION_LENGTH_ESCAPE & 0xff;
        this.#bytes[start + 3] = (INSTRUCTION_LENGTH_ESCAPE >>> 8) & 0xff;
        const total = length + PROTOCOL_ALIGNMENT;
        this.#bytes.splice(start + INSTRUCTION_HEADER_BYTES, 0, total & 0xff, (total >>> 8) & 0xff, (total >>> 16) & 0xff, (total >>> 24) & 0xff);
    }
    u8(value) {
        if (!Number.isInteger(value) || value < 0 || value > 0xff)
            fail("value is outside u8");
        this.#bytes.push(value);
    }
    u16(value) {
        if (!Number.isInteger(value) || value < 0 || value > 0xffff)
            fail("value is outside u16");
        this.#bytes.push(value & 0xff, (value >>> 8) & 0xff);
    }
    u32(value) {
        if (!Number.isInteger(value) || value < 0 || value > 0xffff_ffff)
            fail("value is outside u32");
        this.#bytes.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, value >>> 24);
    }
    f32(value) {
        if (!Number.isFinite(value))
            fail("glyph-resource float must be finite");
        const field = new Uint8Array(4);
        new DataView(field.buffer).setFloat32(0, value, true);
        this.bytes(field);
    }
    bytes(value) {
        for (const byte of value)
            this.#bytes.push(byte);
    }
    pad() {
        while (this.#bytes.length % PROTOCOL_ALIGNMENT !== 0)
            this.#bytes.push(0);
    }
    finish() {
        this.closeInstruction();
        if (this.#bytes.length % PROTOCOL_ALIGNMENT !== 0)
            fail("encoded glyph batch is not aligned");
        return Uint8Array.from(this.#bytes);
    }
}
function nonzeroId(value, label) {
    if (value === 0)
        fail(`glyph ${label} id must be non-zero`);
    return value;
}
function checkedMultiply(left, right) {
    const value = left * right;
    if (!Number.isSafeInteger(value))
        fail("glyph-resource arithmetic overflow");
    return value;
}
function checkedAdd(left, right) {
    const value = left + right;
    if (!Number.isSafeInteger(value))
        fail("glyph-resource arithmetic overflow");
    return value;
}
function fail(message) {
    throw new GlyphResourceError(message);
}
/** Whether an opcode byte names a member this build knows. */
function isKnownOpcode(values, value) {
    return typeof values[value] === "string";
}
//# sourceMappingURL=glyph-resources.js.map